import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useParticipante } from '../../../../contexts/ParticipanteContext';

interface MissaoLocal {
  id: number;
  titulo: string;
  descricao: string;
  xp: number;
  progresso: number;
  meta: number;
  concluida: boolean;
  tipo: 'diaria' | 'semanal' | 'mensal';
}

// Componente de estado vazio reutilizável
function EmptyState({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-20 h-20 flex items-center justify-center bg-slate-100 rounded-full mb-4">
        <i className={`${icon} text-4xl text-slate-300`}></i>
      </div>
      <h3 className="text-lg font-semibold text-slate-700 mb-2">{title}</h3>
      <p className="text-sm text-slate-400 max-w-xs leading-relaxed">{description}</p>
    </div>
  );
}

export default function CampanhaDetalhesPage() {
  const { id } = useParams();
  const { campanhas, todosDistintivos } = useParticipante();

  const [abaAtiva, setAbaAtiva] = useState<'visao-geral' | 'objetivos' | 'missoes' | 'distintivos' | 'recompensas' | 'ranking'>('visao-geral');
  const [faqAberto, setFaqAberto] = useState<number | null>(null);
  const [showRegulamentoCompleto, setShowRegulamentoCompleto] = useState(false);
  const [showMissaoModal, setShowMissaoModal] = useState(false);
  const [missaoSelecionada, setMissaoSelecionada] = useState<MissaoLocal | null>(null);

  // Busca pelo ID como string (IDs são strings no contexto)
  // Nunca usa campanhaAtual como fallback — o ID da URL é a fonte da verdade
  const campanha = campanhas.find(c => String(c.id) === String(id));
  const campanhaVazia = !campanha;
  const isPendente = campanha?.status === 'pendente';

  // Dados reais da campanha (do contexto)
  const progressao = campanha?.progressao;
  const missoesAtivas = progressao?.missoesAtivas ?? [];
  const distintivosConquistados = progressao?.distintivosConquistados ?? [];
  const recompensasDisponiveis = progressao?.recompensasDisponiveis ?? [];
  const rankingCompleto = progressao?.rankingCompleto ?? [];

  // Lista completa de distintivos: todos os base + flag de conquistado da campanha atual
  const todosDistintivosCampanha = useMemo(() => {
    const conquistadosIds = new Set(distintivosConquistados.map((d: any) => d.id));
    return todosDistintivos.map(d => {
      if (conquistadosIds.has(d.id)) {
        const conquistado = distintivosConquistados.find((c: any) => c.id === d.id);
        return { ...d, conquistado: true, dataConquista: conquistado?.dataConquista };
      }
      return { ...d, conquistado: false, dataConquista: undefined };
    });
  }, [todosDistintivos, distintivosConquistados]);

  // Missões locais formatadas para exibição
  const missoesFormatadas: MissaoLocal[] = missoesAtivas.map((m, i) => ({
    id: i + 1,
    titulo: m.nome,
    descricao: m.descricao,
    xp: m.xpRecompensa,
    progresso: m.progresso,
    meta: m.meta,
    concluida: m.concluida,
    tipo: (m.tipo === 'unica' ? 'diaria' : m.tipo) as 'diaria' | 'semanal' | 'mensal',
  }));

  const getTipoMissaoColor = (tipo: string) => {
    const cores: Record<string, string> = {
      'diaria': 'bg-green-100 text-green-700 border-green-300',
      'semanal': 'bg-orange-100 text-orange-700 border-orange-300',
      'mensal': 'bg-slate-100 text-slate-700 border-slate-300',
    };
    return cores[tipo] || 'bg-slate-100 text-slate-700';
  };

  const getRaridadeColor = (raridade: string) => {
    const cores: Record<string, string> = {
      'comum': 'bg-slate-100 text-slate-700 border-slate-300',
      'raro': 'bg-green-100 text-green-700 border-green-300',
      'épico': 'bg-orange-100 text-orange-700 border-orange-300',
      'lendário': 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-700 border-orange-300',
    };
    return cores[raridade] || 'bg-slate-100 text-slate-700';
  };

  const getNivelColor = (nivel: string) => {
    const cores: Record<string, string> = {
      'Bronze': 'bg-amber-100 text-amber-800 border-amber-300',
      'Prata': 'bg-slate-200 text-slate-800 border-slate-400',
      'Ouro': 'bg-yellow-100 text-yellow-800 border-yellow-400',
      'Platina': 'bg-cyan-100 text-cyan-800 border-cyan-400',
      'Diamante': 'bg-orange-100 text-orange-800 border-orange-400',
    };
    return cores[nivel] || 'bg-slate-100 text-slate-800';
  };

  // ─── Campanha não encontrada ───────────────────────────────────────────────
  if (campanhaVazia) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <Link to="/premiado/campanhas" className="inline-flex items-center text-white/90 hover:text-white transition-colors">
              <i className="ri-arrow-left-line mr-2"></i>
              Voltar para Campanhas
            </Link>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-20">
          <div className="max-w-md w-full text-center">
            <div className="w-28 h-28 flex items-center justify-center mx-auto mb-6 bg-orange-50 rounded-full border-2 border-orange-100">
              <i className="ri-file-search-line text-5xl text-orange-300"></i>
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">Campanha não encontrada</h2>
            <p className="text-slate-500 mb-8 leading-relaxed">
              Não encontramos nenhuma informação para esta campanha. Ela pode ter sido encerrada ou o link está incorreto.
            </p>
            <Link
              to="/premiado/campanhas"
              className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Ver minhas campanhas
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── Campanha Pendente ─────────────────────────────────────────────────────
  if (isPendente) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <Link to="/premiado/campanhas" className="inline-flex items-center text-white/90 hover:text-white mb-4 transition-colors">
              <i className="ri-arrow-left-line mr-2"></i>
              Voltar para Campanhas
            </Link>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold">{campanha.nome}</h1>
                  <span className="px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-sm font-bold whitespace-nowrap">Em breve</span>
                </div>
                <p className="text-white/90 text-lg mb-4">{campanha.descricao}</p>
                <div className="flex items-center space-x-6 text-sm">
                  <div className="flex items-center space-x-2">
                    <i className="ri-calendar-line"></i>
                    <span>Início: {new Date(campanha.dataInicio).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="ri-time-line"></i>
                    <span>Término: {new Date(campanha.dataFim).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              </div>
              {/* Card status pendente */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 w-72 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-3">
                  <i className="ri-time-line text-3xl"></i>
                </div>
                <p className="font-bold text-lg mb-1">Aguardando início</p>
                <p className="text-white/80 text-sm">Esta campanha ainda não foi iniciada. Fique de olho!</p>
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo pendente */}
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: 'ri-focus-3-line', label: 'Objetivos', desc: 'Serão definidos ao iniciar' },
              { icon: 'ri-flag-line', label: 'Missões', desc: 'Disponíveis após o início' },
              { icon: 'ri-gift-line', label: 'Recompensas', desc: 'Catálogo em preparação' },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-4 opacity-60">
                <div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-2xl text-slate-400`}></i>
                </div>
                <div>
                  <p className="font-semibold text-slate-700">{item.label}</p>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Regulamento se existir */}
          {campanha.regulamento && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-file-text-line text-orange-500"></i>
                Regulamento da Campanha
              </h2>
              <div className="bg-slate-50 rounded-lg p-5 max-h-80 overflow-y-auto">
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {campanha.regulamento}
                </pre>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-8 text-center">
            <Link
              to="/premiado/campanhas"
              className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Voltar para Campanhas
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── Campanha Ativa ────────────────────────────────────────────────────────
  const missoesConcluidasCount = missoesFormatadas.filter(m => m.concluida).length;
  const totalMissoes = missoesFormatadas.length;
  const distintivosCount = distintivosConquistados.length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Link to="/premiado/campanhas" className="inline-flex items-center text-white/90 hover:text-white mb-4 transition-colors">
            <i className="ri-arrow-left-line mr-2"></i>
            Voltar para Campanhas
          </Link>

          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2">{campanha.nome}</h1>
              <p className="text-white/90 text-lg mb-4">{campanha.descricao}</p>
              <div className="flex items-center space-x-6 text-sm flex-wrap gap-y-2">
                <div className="flex items-center space-x-2">
                  <i className="ri-calendar-line"></i>
                  <span>{new Date(campanha.dataInicio).toLocaleDateString('pt-BR')} - {new Date(campanha.dataFim).toLocaleDateString('pt-BR')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-group-line"></i>
                  <span>{campanha.totalParticipantes} participantes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-trophy-line"></i>
                  <span>Posição #{campanha.ranking}</span>
                </div>
              </div>
            </div>

            {/* Card de Progresso */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 w-80 flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white/80 text-sm mb-1">Seu Progresso</p>
                  <p className="text-3xl font-bold">{campanha.progresso}%</p>
                </div>
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-line-chart-line text-3xl"></i>
                </div>
              </div>
              <div className="w-full bg-white/20 rounded-full h-3 mb-4">
                <div
                  className="bg-white rounded-full h-3 transition-all duration-500"
                  style={{ width: `${campanha.progresso}%` }}
                ></div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-white/70 mb-1">XP Acumulado</p>
                  <p className="font-semibold text-lg">{(progressao?.xpAtual ?? 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-white/70 mb-1">Pontos</p>
                  <p className="font-semibold text-lg">{(progressao?.pontosDisponiveis ?? 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Abas */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex space-x-1 overflow-x-auto">
            {[
              { id: 'visao-geral', label: 'Visão Geral', icon: 'ri-dashboard-line' },
              { id: 'objetivos', label: 'Objetivos', icon: 'ri-focus-3-line' },
              { id: 'missoes', label: 'Missões', icon: 'ri-flag-line' },
              { id: 'distintivos', label: 'Distintivos', icon: 'ri-medal-line' },
              { id: 'recompensas', label: 'Recompensas', icon: 'ri-gift-line' },
              { id: 'ranking', label: 'Ranking', icon: 'ri-bar-chart-line' },
            ].map((aba) => (
              <button
                key={aba.id}
                onClick={() => setAbaAtiva(aba.id as typeof abaAtiva)}
                className={`px-6 py-4 font-medium transition-colors relative whitespace-nowrap flex-shrink-0 ${
                  abaAtiva === aba.id ? 'text-orange-600' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <i className={`${aba.icon} mr-2`}></i>
                {aba.label}
                {abaAtiva === aba.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* ── Visão Geral ── */}
        {abaAtiva === 'visao-geral' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg">
                    <i className="ri-trophy-line text-2xl text-orange-600"></i>
                  </div>
                  <span className="text-2xl font-bold text-slate-900">#{campanha.ranking || '—'}</span>
                </div>
                <p className="text-slate-600 text-sm">Posição no Ranking</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-lg">
                    <i className="ri-star-line text-2xl text-yellow-600"></i>
                  </div>
                  <span className="text-2xl font-bold text-slate-900">{(progressao?.xpAtual ?? 0).toLocaleString()}</span>
                </div>
                <p className="text-slate-600 text-sm">XP Acumulado</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg">
                    <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                  </div>
                  <span className="text-2xl font-bold text-slate-900">
                    {totalMissoes > 0 ? `${missoesConcluidasCount}/${totalMissoes}` : '—'}
                  </span>
                </div>
                <p className="text-slate-600 text-sm">Missões Concluídas</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg">
                    <i className="ri-medal-line text-2xl text-orange-600"></i>
                  </div>
                  <span className="text-2xl font-bold text-slate-900">
                    {distintivosCount > 0 ? distintivosCount : '—'}
                  </span>
                </div>
                <p className="text-slate-600 text-sm">Distintivos Conquistados</p>
              </div>
            </div>

            {/* Missões ativas */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-900">Missões Ativas</h2>
                <button onClick={() => setAbaAtiva('missoes')} className="text-orange-600 hover:text-orange-700 font-medium text-sm whitespace-nowrap">
                  Ver Todas <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
              {missoesFormatadas.filter(m => !m.concluida).length === 0 ? (
                <EmptyState icon="ri-flag-line" title="Nenhuma missão ativa" description="As missões desta campanha ainda não foram configuradas." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {missoesFormatadas.filter(m => !m.concluida).slice(0, 4).map((missao) => (
                    <div key={missao.id} className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition-colors cursor-pointer">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getTipoMissaoColor(missao.tipo)}`}>
                          {missao.tipo.charAt(0).toUpperCase() + missao.tipo.slice(1)}
                        </span>
                        <span className="text-sm font-semibold text-orange-600">+{missao.xp} XP</span>
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1">{missao.titulo}</h3>
                      <p className="text-sm text-slate-600 mb-3">{missao.descricao}</p>
                      <div className="flex items-center justify-between mb-1 text-sm text-slate-600">
                        <span>{missao.progresso} / {missao.meta}</span>
                        <span>{Math.round((missao.progresso / missao.meta) * 100)}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div className="bg-gradient-to-r from-orange-500 to-red-600 h-2 rounded-full" style={{ width: `${(missao.progresso / missao.meta) * 100}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recompensas destaque */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-900">Recompensas em Destaque</h2>
                <button onClick={() => setAbaAtiva('recompensas')} className="text-orange-600 hover:text-orange-700 font-medium text-sm whitespace-nowrap">
                  Ver Todas <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
              {recompensasDisponiveis.length === 0 ? (
                <EmptyState icon="ri-gift-line" title="Nenhuma recompensa disponível" description="O catálogo de recompensas desta campanha ainda não foi configurado." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {recompensasDisponiveis.slice(0, 3).map((rec) => (
                    <div key={rec.id} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="w-full h-32 bg-slate-100">
                        <img src={rec.imagem} alt={rec.nome} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="p-3">
                        <p className="font-semibold text-slate-900 text-sm mb-1 line-clamp-1">{rec.nome}</p>
                        <div className="flex items-center gap-1 text-orange-600 text-sm font-bold">
                          <i className="ri-coin-line"></i>
                          {rec.pontosNecessarios.toLocaleString()} pts
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Regulamento */}
            {campanha.regulamento && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <i className="ri-file-text-line text-orange-500"></i>
                    Regulamento
                  </h2>
                  <button
                    onClick={() => setShowRegulamentoCompleto(!showRegulamentoCompleto)}
                    className="text-orange-600 hover:text-orange-700 font-medium text-sm whitespace-nowrap flex items-center gap-1"
                  >
                    {showRegulamentoCompleto ? 'Ver menos' : 'Ver completo'}
                    <i className={showRegulamentoCompleto ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
                  </button>
                </div>
                <div className={`bg-slate-50 rounded-lg p-5 overflow-hidden transition-all duration-300 ${showRegulamentoCompleto ? '' : 'max-h-40'}`}>
                  <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                    {campanha.regulamento}
                  </pre>
                </div>
                {!showRegulamentoCompleto && (
                  <div className="relative -mt-8 h-8 bg-gradient-to-t from-white to-transparent rounded-b-lg pointer-events-none"></div>
                )}
              </div>
            )}

            {/* FAQ */}
            {campanha.faq && campanha.faq.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-6">
                  <i className="ri-question-answer-line text-orange-500"></i>
                  Perguntas Frequentes
                </h2>
                <div className="space-y-3">
                  {campanha.faq.map((item, index) => (
                    <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setFaqAberto(faqAberto === index ? null : index)}
                        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-semibold text-slate-800 text-sm pr-4">{item.pergunta}</span>
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className={faqAberto === index ? 'ri-arrow-up-s-line text-orange-500 text-lg' : 'ri-arrow-down-s-line text-orange-500 text-lg'}></i>
                        </div>
                      </button>
                      {faqAberto === index && (
                        <div className="px-5 pb-4 border-t border-slate-100">
                          <p className="text-sm text-slate-600 leading-relaxed pt-3">{item.resposta}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Objetivos ── */}
        {abaAtiva === 'objetivos' && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Objetivos da Campanha</h2>
            <EmptyState
              icon="ri-focus-3-line"
              title="Nenhum objetivo configurado"
              description="Os objetivos desta campanha ainda não foram definidos pelo administrador. Eles aparecerão aqui assim que forem cadastrados."
            />
          </div>
        )}

        {/* ── Missões ── */}
        {abaAtiva === 'missoes' && (
          <div className="space-y-6">
            {(['diaria', 'semanal', 'mensal'] as const).map((tipo) => {
              const lista = missoesFormatadas.filter(m => m.tipo === tipo);
              const labels: Record<string, string> = { diaria: 'Missões Diárias', semanal: 'Missões Semanais', mensal: 'Missões Mensais' };
              return (
                <div key={tipo} className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-6">{labels[tipo]}</h2>
                  {lista.length === 0 ? (
                    <EmptyState icon="ri-flag-line" title={`Nenhuma missão ${tipo} disponível`} description="As missões serão exibidas aqui quando forem configuradas." />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {lista.map((missao) => (
                        <div
                          key={missao.id}
                          onClick={() => { setMissaoSelecionada(missao); setShowMissaoModal(true); }}
                          className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition-colors cursor-pointer"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getTipoMissaoColor(missao.tipo)}`}>
                                  {missao.tipo.charAt(0).toUpperCase() + missao.tipo.slice(1)}
                                </span>
                                <span className="text-sm font-semibold text-orange-600">+{missao.xp} XP</span>
                              </div>
                              <h3 className="font-semibold text-slate-900 mb-1">{missao.titulo}</h3>
                              <p className="text-sm text-slate-600">{missao.descricao}</p>
                            </div>
                            {missao.concluida && <i className="ri-checkbox-circle-fill text-2xl text-green-500 ml-4"></i>}
                          </div>
                          <div className="flex items-center justify-between mb-1 text-sm text-slate-600">
                            <span>{missao.progresso} / {missao.meta}</span>
                            <span>{Math.round((missao.progresso / missao.meta) * 100)}%</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${missao.concluida ? 'bg-green-500' : 'bg-gradient-to-r from-orange-500 to-red-600'}`}
                              style={{ width: `${(missao.progresso / missao.meta) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Distintivos ── */}
        {abaAtiva === 'distintivos' && (
          <div className="space-y-6">
            {/* Resumo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg">
                    <i className="ri-medal-line text-2xl text-orange-600"></i>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-slate-900">{distintivosConquistados.length}</div>
                    <div className="text-sm text-slate-500">de {todosDistintivosCampanha.length}</div>
                  </div>
                </div>
                <p className="text-sm font-medium text-slate-700">Conquistados</p>
                <div className="mt-3 bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-orange-500 to-red-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${todosDistintivosCampanha.length > 0 ? (distintivosConquistados.length / todosDistintivosCampanha.length) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg">
                    <i className="ri-star-line text-2xl text-orange-600"></i>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {distintivosConquistados.filter((d: any) => d.raridade !== 'comum').length}
                  </div>
                </div>
                <p className="text-sm font-medium text-slate-700">Raros ou Superior</p>
                <p className="text-xs text-slate-500 mt-1">Épicos e Lendários conquistados</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg">
                    <i className="ri-trophy-line text-2xl text-orange-600"></i>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {todosDistintivosCampanha.length > 0 ? Math.round((distintivosConquistados.length / todosDistintivosCampanha.length) * 100) : 0}%
                  </div>
                </div>
                <p className="text-sm font-medium text-slate-700">Progresso Total</p>
                <p className="text-xs text-slate-500 mt-1">Continue conquistando!</p>
              </div>
            </div>

            {/* Grid de Distintivos */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Distintivos Conquistados</h2>
              {todosDistintivosCampanha.filter(d => d.conquistado).length === 0 && (
                <EmptyState
                  icon="ri-medal-line"
                  title="Nenhum distintivo conquistado ainda"
                  description="Complete missões e objetivos para desbloquear distintivos exclusivos desta campanha."
                />
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {todosDistintivosCampanha.filter(d => d.conquistado).map((d) => {
                  const getCategoriaIcon = (categoria: string) => {
                    switch (categoria) {
                      case 'Iniciante': return 'ri-seedling-line';
                      case 'Vendas': return 'ri-shopping-cart-line';
                      case 'Serviços': return 'ri-customer-service-line';
                      case 'Clientes': return 'ri-user-heart-line';
                      case 'Elite': return 'ri-vip-crown-line';
                      case 'Jogos': return 'ri-gamepad-line';
                      case 'Social': return 'ri-fire-line';
                      default: return 'ri-medal-line';
                    }
                  };
                  const getCategoriaColor = (categoria: string, conquistado: boolean) => {
                    if (!conquistado) return 'bg-slate-400';
                    switch (categoria) {
                      case 'Iniciante': return 'bg-green-500';
                      case 'Vendas': return 'bg-emerald-500';
                      case 'Serviços': return 'bg-violet-500';
                      case 'Clientes': return 'bg-pink-500';
                      case 'Elite': return 'bg-yellow-500';
                      case 'Jogos': return 'bg-indigo-500';
                      case 'Social': return 'bg-red-500';
                      default: return 'bg-orange-500';
                    }
                  };
                  return (
                    <div
                      key={d.id}
                      className={`bg-white rounded-xl border-2 p-6 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 cursor-default ${
                        d.conquistado ? 'border-green-500' : 'border-slate-200 opacity-60 grayscale'
                      }`}
                    >
                      {/* Ícone */}
                      <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${getCategoriaColor(d.categoria, d.conquistado)}`}>
                        <i className={`${getCategoriaIcon(d.categoria)} text-3xl text-white`}></i>
                      </div>
                      {/* Nome */}
                      <h3 className="font-bold text-center text-slate-900 mb-2 line-clamp-2 min-h-[3rem] text-sm">
                        {d.nome}
                      </h3>
                      {/* Raridade */}
                      <div className="flex justify-center mb-3">
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${getRaridadeColor(d.raridade)}`}>
                          {d.raridade.charAt(0).toUpperCase() + d.raridade.slice(1)}
                        </span>
                      </div>
                      {/* Status */}
                      {d.conquistado ? (
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1 text-green-600 font-semibold mb-1">
                            <i className="ri-check-line"></i>
                            <span className="text-xs">Conquistado</span>
                          </div>
                          {d.dataConquista && (
                            <div className="text-xs text-slate-500">{d.dataConquista}</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center text-xs text-slate-500">
                          <i className="ri-lock-line mr-1"></i>
                          Bloqueado
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Recompensas ── */}
        {abaAtiva === 'recompensas' && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Recompensas Disponíveis</h2>
              <div className="flex items-center space-x-2 bg-orange-100 px-4 py-2 rounded-lg">
                <i className="ri-coin-line text-orange-600"></i>
                <span className="font-semibold text-orange-600">{(progressao?.pontosDisponiveis ?? 0).toLocaleString()} pontos</span>
              </div>
            </div>
            {recompensasDisponiveis.length === 0 ? (
              <EmptyState
                icon="ri-gift-line"
                title="Nenhuma recompensa disponível"
                description="O catálogo de recompensas desta campanha ainda não foi configurado pelo administrador."
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {recompensasDisponiveis.map((rec) => {
                  const podeResgatar = (progressao?.pontosDisponiveis ?? 0) >= rec.pontosNecessarios;
                  return (
                    <div key={rec.id} className={`border rounded-xl overflow-hidden transition-all ${podeResgatar ? 'border-slate-200 hover:border-orange-300' : 'border-slate-200 opacity-60'}`}>
                      <div className="relative w-full h-48 bg-slate-100">
                        <img src={rec.imagem} alt={rec.nome} className="w-full h-full object-cover object-top" />
                        {!podeResgatar && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <div className="text-center text-white">
                              <i className="ri-lock-line text-3xl mb-1"></i>
                              <p className="text-sm font-medium">Pontos Insuficientes</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">{rec.categoria}</span>
                        <h3 className="font-bold text-slate-900 mt-3 mb-1">{rec.nome}</h3>
                        <p className="text-sm text-slate-600 mb-4">{rec.descricao}</p>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-1">
                            <i className="ri-coin-line text-orange-600"></i>
                            <span className="font-bold text-orange-600">{rec.pontosNecessarios.toLocaleString()}</span>
                          </div>
                          <span className="text-sm text-slate-600">{rec.quantidadeDisponivel} disponíveis</span>
                        </div>
                        <button
                          disabled={!podeResgatar}
                          className={`w-full py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${podeResgatar ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:opacity-90' : 'bg-slate-200 text-slate-500 cursor-not-allowed'}`}
                        >
                          {podeResgatar ? 'Resgatar' : 'Pontos Insuficientes'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Ranking ── */}
        {abaAtiva === 'ranking' && (
          <div className="space-y-6">
            {rankingCompleto.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-6">Ranking da Campanha</h2>
                <EmptyState
                  icon="ri-bar-chart-line"
                  title="Ranking ainda não disponível"
                  description="O ranking será exibido assim que os participantes começarem a acumular pontos nesta campanha."
                />
              </div>
            ) : (
              <>
                {/* Top 3 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {rankingCompleto.slice(0, 3).map((p, index) => {
                    const gradientes = ['from-yellow-400 to-yellow-600', 'from-slate-300 to-slate-500', 'from-amber-600 to-amber-800'];
                    const medalhas = ['🥇', '🥈', '🥉'];
                    return (
                      <div key={p.posicao} className={`bg-gradient-to-br ${gradientes[index]} rounded-xl p-6 text-white`}>
                        <div className="text-center">
                          <div className="text-4xl mb-4">{medalhas[index]}</div>
                          <div className="w-24 h-24 flex items-center justify-center mx-auto mb-4 rounded-full border-4 border-white overflow-hidden">
                            <img src={p.participante.avatar} alt={p.participante.nome} className="w-full h-full object-cover object-top" />
                          </div>
                          <h3 className="font-bold text-xl mb-2">{p.participante.nome}</h3>
                          <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">{p.participante.nivel}</span>
                          <div className="mt-4 pt-4 border-t border-white/20">
                            <p className="text-sm opacity-90 mb-1">XP Acumulado</p>
                            <p className="text-2xl font-bold">{p.xp.toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Lista */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-6">Ranking Completo</h2>
                  <div className="space-y-3">
                    {rankingCompleto.map((p) => {
                      const isVoce = p.posicao === campanha.ranking;
                      return (
                        <div key={p.posicao} className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${isVoce ? 'bg-orange-50 border-orange-300' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                          <div className="flex items-center space-x-4 flex-1">
                            <div className="w-12 h-12 flex items-center justify-center font-bold text-slate-900">
                              {p.posicao <= 3 ? <span className="text-2xl">{['🥇', '🥈', '🥉'][p.posicao - 1]}</span> : <span className="text-lg">#{p.posicao}</span>}
                            </div>
                            <div className="w-12 h-12 flex items-center justify-center rounded-full overflow-hidden border-2 border-slate-200">
                              <img src={p.participante.avatar} alt={p.participante.nome} className="w-full h-full object-cover object-top" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h3 className="font-semibold text-slate-900">{p.participante.nome}</h3>
                                {isVoce && <span className="px-2 py-1 bg-orange-500 text-white rounded text-xs font-medium">Você</span>}
                              </div>
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium border mt-1 ${getNivelColor(p.participante.nivel)}`}>
                                {p.participante.nivel}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-orange-600">{p.xp.toLocaleString()}</p>
                            <p className="text-sm text-slate-600">XP</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Modal Missão */}
      {showMissaoModal && missaoSelecionada && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Detalhes da Missão</h2>
              <button onClick={() => setShowMissaoModal(false)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-6">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTipoMissaoColor(missaoSelecionada.tipo)}`}>
                  {missaoSelecionada.tipo.charAt(0).toUpperCase() + missaoSelecionada.tipo.slice(1)}
                </span>
                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">+{missaoSelecionada.xp} XP</span>
                {missaoSelecionada.concluida && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    <i className="ri-checkbox-circle-fill mr-1"></i>Concluída
                  </span>
                )}
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">{missaoSelecionada.titulo}</h3>
              <p className="text-slate-600 mb-6">{missaoSelecionada.descricao}</p>
              <div className="bg-slate-50 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-600">Progresso</span>
                  <span className="text-2xl font-bold text-slate-900">{Math.round((missaoSelecionada.progresso / missaoSelecionada.meta) * 100)}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-4 mb-3">
                  <div
                    className={`h-4 rounded-full ${missaoSelecionada.concluida ? 'bg-green-500' : 'bg-gradient-to-r from-orange-500 to-red-600'}`}
                    style={{ width: `${(missaoSelecionada.progresso / missaoSelecionada.meta) * 100}%` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span>{missaoSelecionada.progresso} concluído</span>
                  <span>{missaoSelecionada.meta} meta</span>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4">
              <button onClick={() => setShowMissaoModal(false)} className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all whitespace-nowrap">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
